import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import api from "../api/axios"
import { switchGym as switchGymApi } from "../services/gymService"

const STORAGE_KEY = "gymflow_auth"

const AuthContext = createContext(null)

function readStoredSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data || typeof data !== "object") return null
    if (data.token && data.user?.email) {
      return {
        token: data.token,
        user: data.user,
        loggedAt: data.loggedAt,
      }
    }
    return null
  } catch {
    return null
  }
}

function persistSession(next) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => readStoredSession())

  const user = session?.user ?? null
  const isAuthenticated = Boolean(session?.token && user?.email)
  const gyms = user?.gyms ?? []
  const activeGym = user?.gym ?? null

  const applySession = useCallback((data, reload = false) => {
    const next = {
      token: data.token,
      user: data.user,
      loggedAt: Date.now(),
    }
    persistSession(next)
    setSession(next)
    if (reload) {
      window.location.assign("/")
    }
  }, [])

  const refreshSession = useCallback(async () => {
    if (!session?.token) return
    try {
      const { data } = await api.get("/me")
      setSession((prev) => {
        if (!prev?.token) return prev
        const next = { ...prev, user: data, loggedAt: Date.now() }
        persistSession(next)
        return next
      })
    } catch {
      // 401 géré par l’intercepteur axios
    }
  }, [session?.token])

  useEffect(() => {
    if (!session?.token || !session.user?.email) return
    const needsRefresh = !Array.isArray(session.user.gyms) || session.user.gyms.length === 0
    if (needsRefresh) {
      void refreshSession()
    }
  }, [session?.token, session?.user?.email, session?.user?.gyms, refreshSession])

  const login = useCallback(async (email, password) => {
    const e = String(email || "").trim()
    const p = String(password || "")
    if (!e || !p) {
      throw new Error("missing")
    }
    const { data } = await api.post("/login", { email: e, password: p })
    applySession(data)
  }, [applySession])

  const switchGym = useCallback(async (gymId) => {
    const data = await switchGymApi(gymId)
    applySession(data, true)
  }, [applySession])

  const updateUser = useCallback((nextUser) => {
    setSession((prev) => {
      if (!prev) return prev
      const next = { ...prev, user: nextUser }
      persistSession(next)
      return next
    })
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    setSession(null)
  }, [])

  const value = useMemo(
    () => ({
      user,
      token: session?.token ?? null,
      isAuthenticated,
      gyms,
      activeGym,
      login,
      logout,
      switchGym,
      updateUser,
    }),
    [user, session?.token, isAuthenticated, gyms, activeGym, login, logout, switchGym, updateUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth doit être utilisé dans un AuthProvider")
  }
  return ctx
}
